import {
  CoogoStatusMark,
  type CoogoMarkLayer,
  type CoogoMarkPace,
  type CoogoMarkSize,
  type CoogoMarkState,
} from './coogo-status-mark'

import './coogo-count-badge.css'

export type CoogoCountBadgeSize = 'standard' | 'compact' | 'micro'

export interface CoogoCountBadgeProps {
  count: number
  unit: string
  size?: CoogoCountBadgeSize
  label?: string
  announce?: boolean
  className?: string
}

export interface CoogoMarkCountProps extends CoogoCountBadgeProps {
  state: CoogoMarkState
  statusLabel: string
  /**
   * F9(b) — the WHOLE accessible name, authored in one language by the
   * caller's copy module. This component used to build the announcement by
   * concatenating the status label, the count and the unit, which announced
   * "Generated app. 10 페이지": an English producer literal glued to a Korean
   * unit. Word order and punctuation are not the same in every language, so
   * the phrase is no longer assembled here out of fragments. Required, not
   * optional — an optional one would be forgotten exactly the way F10's
   * `a11yTitle` was.
   */
  accessibleLabel: string
  layer?: CoogoMarkLayer
  pace?: CoogoMarkPace
  markSize?: CoogoMarkSize
}

function normalizeCount(count: number) {
  return Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0
}

export function CoogoCountBadge({
  count,
  unit,
  size = 'compact',
  label,
  announce = true,
  className,
}: CoogoCountBadgeProps) {
  const normalizedCount = normalizeCount(count)
  const accessibleLabel = label ?? `${normalizedCount} ${unit}`

  return (
    <span
      className={['coogo-count-badge', className].filter(Boolean).join(' ')}
      data-role="secondary-count"
      data-size={size}
      data-testid="coogo-count-badge"
      role={announce ? 'status' : undefined}
      aria-live={announce ? 'polite' : undefined}
      aria-label={announce ? accessibleLabel : undefined}
      aria-hidden={announce ? undefined : true}
    >
      <span className="coogo-count-badge__orb" data-testid="coogo-count-orb">
        {normalizedCount}
      </span>
      <span className="coogo-count-badge__unit">{unit}</span>
    </span>
  )
}

/**
 * Hybrid placement for the few surfaces that need both Coogo status and a
 * real work quantity. The operational mark owns state and pace; the neutral
 * badge is deliberately secondary and only carries the count.
 */
export function CoogoMarkCount({
  count,
  unit,
  state = 'idle',
  size = 'compact',
  statusLabel,
  accessibleLabel,
  layer = 'overall',
  pace = 'normal',
  markSize = 'inline-compact',
  className,
}: CoogoMarkCountProps) {
  const normalizedCount = normalizeCount(count)

  return (
    <span
      className={['coogo-mark-count', className].filter(Boolean).join(' ')}
      data-state={state}
      data-testid="coogo-mark-count"
      role="status"
      aria-live="polite"
      aria-label={accessibleLabel}
    >
      <CoogoStatusMark
        mode="activity"
        state={state}
        layer={layer}
        pace={pace}
        size={markSize}
        label=""
      />
      <CoogoCountBadge
        count={normalizedCount}
        unit={unit}
        size={size}
        announce={false}
      />
      <span className="coogo-mark-count__label" title={statusLabel}>
        {statusLabel}
      </span>
    </span>
  )
}
